// Claim-profile helpers for the Apps → Token & Claims panel.
//
// MIRRORS services/common.utils.js (CLAIM_SOURCES / RESERVED_CLAIM_NAMES /
// validateClaimProfile). The API validates every save regardless — this copy
// exists so the form can show the same problems inline instead of round-tripping
// to a 400. Keep the two in sync; the server is always the authority.

// The identity values UM can emit under an app-chosen claim name. Anything not
// listed here is operational and passes through untouched.
export const CLAIM_SOURCES = [
    {
        key: 'user_id',
        label: 'User ID',
        suggestion: 'sub',
        hint: 'The subject of the token. `sub` is the standard JWT claim.',
    },
    {
        key: 'tenant_id',
        label: 'Tenant ID',
        suggestion: 'tenantId',
        hint: "The tenant the user is scoped to. Pass through UM's id, or transform it below.",
    },
    {
        key: 'tenant_slug',
        label: 'Tenant Slug',
        suggestion: 'tenantSlug',
        hint: 'URL-safe tenant key (tenants.tenant_slug), falling back to the tenant name.',
    },
    { key: 'roles', label: 'Roles', suggestion: 'roles', hint: "The user's roles for this app." },
    {
        key: 'provider_org_id',
        label: 'Provider Org ID',
        suggestion: 'providerOrgId',
        hint: 'Owning organisation of an external provider user. Needs External Users enabled.',
    },
];

// Claims the token always carries. Remapping onto one of these silently
// overwrites it and breaks session handling downstream.
export const RESERVED_CLAIM_NAMES = [
    'username',
    'email_id',
    'app_id',
    'session_id',
    'source',
    'requesting_user_id',
    'first_name',
    'last_name',
    'gender',
    'phone_no',
    'login_source',
    'is_portal_admin',
    'component_details',
    'session_idle_timeout_minutes',
    'iss',
    'aud',
    'exp',
    'iat',
    'nbf',
    'jti',
];

export const TENANT_ID_TRANSFORMS = [
    { value: 'none', label: 'None — pass UM’s tenant id through' },
    { value: 'uuidv5', label: 'UUIDv5 — derive a stable UUID under a namespace' },
];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CLAIM_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const isUuid = (v) => UUID_RE.test(String(v || ''));

/** A default profile that is valid the moment remapping is switched on. */
export const defaultClaimProfile = () => ({
    name: 'mapped',
    claim_map: { user_id: 'sub' },
    retain_claims: [],
    drop_claims: [],
    roles_as_names: true,
    tenant_id_transform: { type: 'none' },
});

/**
 * Same rules as the server's validateClaimProfile.
 * @returns {string[]} problems, empty when valid
 */
export const validateClaimProfile = (profile) => {
    const errors = [];
    if (profile === undefined || profile === null || typeof profile === 'string') return errors;
    if (typeof profile !== 'object' || Array.isArray(profile)) return ['Claim profile must be an object.'];

    const map = profile.claim_map && typeof profile.claim_map === 'object' ? profile.claim_map : {};
    const sourceKeys = CLAIM_SOURCES.map((s) => s.key);
    const seen = [];

    Object.entries(map).forEach(([source, target]) => {
        if (!sourceKeys.includes(source)) {
            errors.push(`"${source}" is not a mappable value.`);
            return;
        }
        const name = typeof target === 'string' ? target.trim() : '';
        if (!name) return; // blank input = simply not emitted
        if (!CLAIM_NAME_RE.test(name)) {
            errors.push(`"${name}" is not a valid claim name (letters, digits and _ only, not starting with a digit).`);
            return;
        }
        if (RESERVED_CLAIM_NAMES.includes(name)) errors.push(`"${name}" is a reserved claim and cannot be reused.`);
        if (seen.includes(name)) errors.push(`Two values are both mapped to "${name}".`);
        seen.push(name);
    });

    const transform = profile.tenant_id_transform || null;
    const transformType = transform ? String(transform.type || 'none') : 'none';
    if (transform && !TENANT_ID_TRANSFORMS.some((t) => t.value === transformType)) {
        errors.push(`Unknown tenant id transform "${transformType}".`);
    }
    if (transformType === 'uuidv5' && !isUuid(transform.namespace)) {
        errors.push('A UUIDv5 transform needs a valid namespace UUID.');
    }

    const pa = profile.platform_admin || null;
    if (pa && !(typeof pa.grant_role === 'string' && pa.grant_role.trim())) {
        errors.push('The platform-admin binding needs a role to grant.');
    }

    // HARD rule: UM's own auth middleware resolves role mappings by the NATIVE
    // tenant_id on every UM-token round-trip (reset-password, verify-OTP,
    // change-password, renew-token) and cannot reverse a one-way transform.
    const retains = Array.isArray(profile.retain_claims) ? profile.retain_claims : [];
    const remapsTenantId = Boolean(map.tenant_id && String(map.tenant_id).trim());
    if ((remapsTenantId || transformType !== 'none') && !retains.includes('tenant_id')) {
        errors.push(
            'Keep "tenant_id" under its original name — UM resolves role mappings by the native tenant id, ' +
                'so remapping it without retaining it breaks password reset, OTP verify and token renewal.'
        );
    }
    if (Array.isArray(profile.drop_claims) && profile.drop_claims.includes('tenant_id')) {
        errors.push('"tenant_id" cannot be dropped.');
    }

    return errors;
};

/** Preview the identity claim names a token will carry under this profile. */
export const previewClaimNames = (profile) => {
    if (!profile || typeof profile !== 'object') return [];
    const map = profile.claim_map || {};
    const names = CLAIM_SOURCES.map((s) => (map[s.key] || '').trim()).filter(Boolean);
    (profile.retain_claims || []).forEach((c) => {
        if (!names.includes(c)) names.push(c);
    });
    return names;
};

/** RFC 4122 v4 UUID for seeding a namespace, using the Web Crypto API. */
export const generateUuid = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    /* eslint-disable no-bitwise */
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    /* eslint-enable no-bitwise */
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
